import { Component, HostListener, inject, Input, OnInit } from '@angular/core';
import {
  AaNgModule,
  DialogContentService,
  ISTPersonActivity,
  PERSON_URL_PIPE_TOKEN,
} from 'aa-ng';
import {
  AaNgBootstrapModule,
  IActivityCollaboratorSort,
} from 'aa-ng-bootstrap';
import { catchError, EMPTY, Subject, switchMap } from 'rxjs';
import {
  ActivityCollaboratorSortProperty,
  IActivityCollaborator,
} from 'src/app/models';

import { PeopleService } from '../../services';
import { BaseDirective } from '../base.component';

/**
 * The full collaborator list for one activity, paged from the server.
 *
 * Every surface that lists activities shows at most a handful of scholars inline and offers this for
 * the rest, so the whole author list is never fetched to render ten names. Search, sort and paging all
 * go back to the endpoint rather than filtering an array in memory - the list can be hundreds long on
 * a hyperauthored work, which is the case this exists for.
 */
@Component({
  selector: 'app-activity-collaborators-modal',
  imports: [AaNgBootstrapModule, AaNgModule],
  template: `
    @if (activity) {
      <div class="collaborators-modal-header">
        <aa-activity-header
          class="no-padding"
          [activity]="activity"
        ></aa-activity-header>
      </div>
      <div class="collaborators-modal-body aa-collaborators-scope">
        <aa-activity-collaborators
          [rows]="rows"
          [totalCount]="totalCount"
          [loading]="loading"
          [loadError]="loadError"
          [pageNumber]="pageNumber"
          [pageSize]="pageSize"
          [pageSizeOptions]="pageSizeOptions"
          [personRouteFactory]="personRouteFactory"
          (searchChange)="onSearch($event)"
          (pageNumberChange)="onPageNumber($event)"
          (pageSizeChange)="onPageSize($event)"
          (sortChange)="onSort($event)"
          (profileLinkClick)="closeDialog()"
        ></aa-activity-collaborators>
      </div>
    }
  `,
})
export class ActivityCollaboratorsModalComponent
  extends BaseDirective
  implements OnInit
{
  private peopleService = inject(PeopleService);
  private dialogContentService = inject(DialogContentService);
  private personUrlPipe = inject(PERSON_URL_PIPE_TOKEN) as {
    transform: (person: unknown) => unknown[];
  };

  @Input() activity: ISTPersonActivity;

  /**
   * Keeps this modal's own clicks off the document.
   *
   * Every open dialog carries a document-level click-outside listener, and each one answers by
   * closing the *topmost* dialog - so a click in here, which is by definition outside the dialog
   * this modal opened on top of, closed this modal. The page-size dropdown is where it showed: the
   * native option popup reports coordinates the dialog underneath does not contain, while the
   * paginate controls escaped only because that listener ignores anchors. Clicking genuinely
   * outside this modal still reaches the document and still closes it.
   */
  @HostListener('click', ['$event'])
  onHostClick(event: MouseEvent) {
    event.stopPropagation();
  }

  rows: IActivityCollaborator[] = [];
  totalCount = 0;
  loading = false;
  loadError = false;

  pageSizeOptions = [10, 20, 50];

  /** 1-based, because that is what the shared table emits. The endpoint is 0-based. */
  pageNumber = 1;
  pageSize = 20;

  private _searchTerm = '';
  private _sortProperty: ActivityCollaboratorSortProperty = 'authorOrder';
  private _sortDescending = false;
  private _fetch$ = new Subject<void>();

  ngOnInit(): void {
    this._subscriptions.push(
      this._fetch$
        .pipe(
          // switchMap, so a superseded request cannot land after a newer one and overwrite the
          // rows. Typing in the search box or paging quickly would otherwise leave whichever
          // response happened to arrive last on screen - stale data, with nothing to signal it.
          switchMap(() => {
            if (!this.activity?.guid) {
              return EMPTY;
            }

            this.loading = true;
            this.loadError = false;

            return this.peopleService
              .getCollaboratorsByActivityGuidPaged(this.activity.guid, {
                pageIndex: this.pageNumber - 1,
                pageSize: this.pageSize,
                searchTerm: this._searchTerm,
                sortProperty: this._sortProperty,
                sortDescending: this._sortDescending,
              })
              .pipe(
                // Caught inside the projection on purpose: an error escaping to the outer
                // stream would complete it, and the modal would silently stop responding to
                // paging and search for the rest of its life.
                catchError((err) => {
                  console.error('ActivityCollaboratorsModal error', err);
                  this.rows = [];
                  this.totalCount = 0;
                  this.loading = false;
                  this.loadError = true;
                  return EMPTY;
                }),
              );
          }),
        )
        .subscribe((resultSet) => {
          this.rows = resultSet?.results ?? [];
          this.totalCount = resultSet?.totalResults ?? 0;
          this.loading = false;
        }),
    );

    this._fetch$.next();
  }

  onSearch(query: string) {
    this._searchTerm = (query ?? '').trim();
    this.pageNumber = 1;
    this._fetch$.next();
  }

  onSort(sort: IActivityCollaboratorSort) {
    if (!sort?.property) {
      return;
    }

    // Anything the endpoint does not sort on falls back to author order rather than silently
    // returning the same page under a different header.
    this._sortProperty =
      sort.property.toLowerCase() === 'authororder' ? 'authorOrder' : 'name';
    this._sortDescending = sort.descending;
    this.pageNumber = 1;
    this._fetch$.next();
  }

  onPageNumber(pageNumber: number) {
    this.pageNumber = pageNumber;
    this._fetch$.next();
  }

  onPageSize(pageSize: number) {
    this.pageSize = pageSize;
    this.pageNumber = 1;
    this._fetch$.next();
  }

  /**
   * Deliberately not named `close`: `DialogsComponent.update()` treats any truthy
   * `componentRef.instance.close` as a self-close `EventEmitter` and calls `.subscribe()` on it
   * unconditionally - a same-named plain method here is truthy too, and crashes that check.
   */
  closeDialog() {
    this.dialogContentService.closeTopmostDialog();
  }

  /**
   * The shared table asks for each row's profile route. Rows with no `id` have no in-group profile to
   * link to; the server also sets `displayAsPlainText` on them, so the table renders a name instead.
   */
  personRouteFactory = (row: IActivityCollaborator): unknown[] =>
    row?.id ?
      this.personUrlPipe.transform({
        id: row.id,
        firstName: row.firstName,
        lastName: row.lastName,
      })
    : [];
}
